import * as ngCore from '@angular/core'
import * as ngMatDatepicker from '@angular/material/datepicker'
import { Multirange } from '@attain-apps/range'

import * as config from '../../../../config'
import * as input from '../input'
import * as models from '../models'
import * as overlay from '../overlay'
import { Store } from '../store'

import * as multiRangeHeader from './header'
import * as multiRangeInput from './input'
import { ListStore } from './store'
/**
 * @example
 * ```html
 * <atn-ds-date-range-multi
 *  label="Some label"
 *  [(value)]="dateRange"
 * />
 * ```
 */
@ngCore.Component({
  imports: [
    overlay.Component,
    input.Component,
    multiRangeInput.Component,
    multiRangeHeader.Component,
  ],
  providers: [
    Store,
    ListStore,
    ...config.dateFormat.DATE_MATERIAL_PROVIDERS,
    // Override Angular Material's default date range selection strategy with a
    // custom implementation defined in `overlay.Component`.
    //
    // This allows us to customize how date ranges are calculated when users
    // interact with the calendar (e.g., controlling how start/end dates are
    // selected, applying logic for custom input fields, etc.).
    {
      provide: ngMatDatepicker.MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: overlay.Component,
    },
  ],
  selector: 'atn-ds-date-range-multi',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link ListStore} */
  public readonly listStore = ngCore.inject(ListStore)

  /** @see {@link Store} */
  public readonly store = ngCore.inject(Store)

  /** Label text displayed above the date range picker input. */
  public readonly labelSg = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })

  /** Holds the date range list input value. */
  public readonly valueSg = ngCore.model<Multirange<Date> | undefined>(
    undefined,
    {
      alias: 'value',
    },
  )

  /** Converts persisted date-only ranges to the representation used by the picker. */
  public readonly displayValueSg = ngCore.computed((value = this.valueSg()) =>
    models.DateMultirange.normalizeToLocal(value),
  )

  constructor() {
    // Update the store with new ranges whenever the user (i.e., the parent component)
    // provides new input to this component
    ngCore.effect(() => {
      const multirange = this.displayValueSg()
      // patchState makes every property optional, so even though 'ranges' is
      // required in state, we use explicit nullish coalescing to ensure it's
      // always set to an array (never undefined).
      this.listStore.patchState({ ranges: multirange?.ranges ?? [] })
      // NOTE: There's a potential edge case where, during the editing period,
      // a new list of ranges might be received. To handle this, we can use the
      // following approach:
      // ngCore.untracked(() => {
      // const isEditing = this.listStore.isEditingSg();
      // if (isEditing) {
      //   // Sync editingRanges with the newly received ranges
      //   this.store.patchState({ editingRanges: [...ranges] });
      // }
      // });
    })

    this.store.patchState({
      type: 'multirange',
    })
  }

  /**
   *  Opens the date range picker and initializes it with the selected ranges
   */
  public openDateRangePickerHandler(
    picker: ngMatDatepicker.MatDateRangePicker<Date>,
  ) {
    this.listStore.startEditing()
    picker?.open()
  }

  /** Outputs the currently edited ranges */
  public applyChangesHandler() {
    this.listStore.finishEditing()
    const multirange = new Multirange<Date>({
      ranges: this.listStore.rangesSg(),
    })
    this.valueSg.set(models.DateMultirange.normalizeToUtc(multirange))
  }

  /** Resets the currently selected ranges */
  public discardChangesHandler() {
    this.listStore.discardEditing()
  }
}
